import React from 'react';

const QuestionPalette = ({ 
  exam,
  questions,
  answers,
  markedForReview,
  visited,
  currentQuestionIndex,
  currentQuestion,
  goToQuestion,
  setShowSubmitConfirm,
  getQuestionStatus,
  getStatusColor
}) => {
  return (
    <div className="bg-white rounded-xl shadow-lg p-6 sticky top-24">
      <h3 className="font-bold text-gray-900 mb-4">Question Palette</h3>
      
      {/* Section Navigation (if multiple sections) */}
      {exam?.sections && exam.sections.length > 1 && (
        <div className="mb-4 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
          <h4 className="text-xs font-semibold text-gray-900 mb-2">Jump to Section:</h4>
          <div className="flex flex-col gap-2">
            {exam.sections.map((section, displaySectionIndex) => {
              // Questions now have updated sectionId after shuffling
              const sectionQuestions = questions.filter(q => (q.sectionId || 0) === displaySectionIndex);
              const firstQuestionIndex = questions.findIndex(q => (q.sectionId || 0) === displaySectionIndex);
              const sectionAnswered = sectionQuestions.filter(q => answers[q.id] && answers[q.id] !== '').length;
              
              if (sectionQuestions.length === 0) return null;
              
              // Get section name from first question with this sectionId (has the shuffled section name)
              const sectionName = sectionQuestions[0]?.sectionName || section.name || `Section ${displaySectionIndex + 1}`;
              
              return (
                <button
                  key={displaySectionIndex}
                  onClick={() => firstQuestionIndex !== -1 && goToQuestion(firstQuestionIndex)}
                  className={`px-3 py-2 rounded-lg font-semibold transition border-2 text-left ${
                    (currentQuestion.sectionId || 0) === displaySectionIndex
                      ? 'bg-purple-600 text-white border-purple-700'
                      : 'bg-white text-purple-600 border-purple-300 hover:bg-purple-50'
                  }`}
                >
                  <div className="text-xs font-bold">{sectionName}</div>
                  <div className="text-xs opacity-90">{sectionAnswered}/{sectionQuestions.length} answered</div>
                </button>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Legend */}
      <div className="grid grid-cols-2 gap-2 mb-4 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-green-500 border-2 border-green-600 rounded flex items-center justify-center text-white font-bold text-xs">1</div>
          <span className="text-gray-700 font-medium">Answered</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-red-500 border-2 border-red-600 rounded flex items-center justify-center text-white font-bold text-xs">2</div>
          <span className="text-gray-700 font-medium">Not Answered</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-purple-500 border-2 border-purple-600 rounded flex items-center justify-center text-white font-bold text-xs">3</div>
          <span className="text-gray-700 font-medium">Marked for Review</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-gradient-to-r from-green-500 to-purple-500 border-2 border-purple-600 rounded flex items-center justify-center text-white font-bold text-xs">4</div>
          <span className="text-gray-700 font-medium">Answered & Marked</span>
        </div>
        <div className="flex items-center gap-2 col-span-2">
          <div className="w-6 h-6 bg-gray-200 border-2 border-gray-300 rounded flex items-center justify-center text-gray-600 font-bold text-xs">5</div>
          <span className="text-gray-700 font-medium">Not Visited</span>
        </div>
      </div>

      {/* Section-wise Question Grid */}
      {exam?.sections && exam.sections.length > 1 ? (
        <div className="space-y-4">
          {exam.sections.map((section, displaySectionIndex) => {
            // Get questions for this display section index (after shuffling, questions have new sectionIds)
            const sectionQuestions = questions
              .map((q, idx) => ({ question: q, questionIndex: idx }))
              .filter(item => (item.question.sectionId || 0) === displaySectionIndex)
              .sort((a, b) => a.questionIndex - b.questionIndex); // Keep in shuffled order
            
            if (sectionQuestions.length === 0) return null;
            
            // Get section name from first question (it has the shuffled section name)
            const sectionName = sectionQuestions[0]?.question.sectionName || section.name || `Section ${displaySectionIndex + 1}`;
            
            return (
              <div key={displaySectionIndex} className="border-b border-gray-200 pb-4 last:border-0">
                <h4 className="text-sm font-bold text-gray-900 mb-3 bg-gradient-to-r from-purple-100 to-pink-100 px-3 py-2 rounded-lg">
                  {sectionName}
                </h4>
                <div className="grid grid-cols-4 gap-2">
                  {sectionQuestions.map(({ questionIndex }) => {
                    const status = getQuestionStatus(questionIndex);
                    // Display number is just questionIndex + 1 for sequential numbering
                    const displayNum = questionIndex + 1;
                    return (
                      <button
                        key={questionIndex}
                        onClick={() => goToQuestion(questionIndex)}
                        className={`aspect-square rounded-lg font-bold border-2 transition text-xs ${getStatusColor(status)}`}
                      >
                        {displayNum}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-2">
          {questions.map((_, index) => {
            const status = getQuestionStatus(index);
            // Display number is just index + 1 for sequential numbering
            const displayNum = index + 1;
            return (
              <button
                key={index}
                onClick={() => goToQuestion(index)}
                className={`aspect-square rounded-lg font-bold border-2 transition ${getStatusColor(status)}`}
              >
                {displayNum}
              </button>
            );
          })}
        </div>
      )}

      {/* Submit Button */}
      <button
        onClick={() => setShowSubmitConfirm(true)}
        className="w-full mt-6 px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-bold hover:shadow-lg transition"
      >
        Submit Exam
      </button>
    </div>
  );
};

export default QuestionPalette;
